import React from 'react';

import { Box } from '@components/Restyle';
import { Skeleton } from '@components/shared/Skeleton';
import { MemberRowSkeleton, SkeletonList } from '@components/shared/skeletons';
import theme from '@themes/theme';

/** `ProjectForm`'s TextField inputs, address row and selectors are all 48 high. */
const FIELD_HEIGHT = 48;
/** The green UserPlus circle in `AddMembersSection`'s project flow. */
const INVITE_CIRCLE_SIZE = 40;

const LabelledField: React.FC<{ labelWidth: number }> = ({ labelWidth }) => (
  <Box marginBottom='m'>
    <Skeleton variant='text' width={labelWidth} />
    <Box marginTop='xs'>
      <Skeleton
        variant='rect'
        width='100%'
        height={FIELD_HEIGHT}
        borderRadius='xs'
      />
    </Box>
  </Box>
);

const renderMemberRowSkeleton = (index: number) => (
  <Box key={index} marginBottom='s'>
    <MemberRowSkeleton />
  </Box>
);

/**
 * Placeholder for the mobile Edit Project form (TA-10386), mirroring
 * `ProjectForm.tsx`: name, address, description, the team selector button, the
 * colour and icon selectors, then the members section's Invite row and member
 * rows.
 *
 * `EditProject.tsx` can't mount `ProjectFormStack` before `getProject`
 * resolves — Formik's `initialValues` are derived from the project — so this
 * stands in for the whole form rather than for individual fields. It replaces
 * a bare centred spinner.
 */
export const ProjectFormSkeleton: React.FC<{ testID?: string }> = ({
  testID = 'project-form-skeleton',
}) => {
  return (
    <Box testID={testID} flex={1} marginHorizontal='m' marginTop='l'>
      <LabelledField labelWidth={60} />
      <LabelledField labelWidth={70} />
      <LabelledField labelWidth={90} />

      {/* Team label + the full-width team selector button. */}
      <LabelledField labelWidth={50} />

      {/* Colour and icon selectors, half width each. */}
      <Box flexDirection='row' marginBottom='m'>
        <Box width='50%' paddingRight='m'>
          <Skeleton variant='text' width={50} />
          <Box marginTop='xs'>
            <Skeleton
              variant='rect'
              width='100%'
              height={FIELD_HEIGHT}
              borderRadius='xs'
            />
          </Box>
        </Box>
        <Box width='50%' paddingLeft='m'>
          <Skeleton variant='text' width={40} />
          <Box marginTop='xs'>
            <Skeleton
              variant='rect'
              width='100%'
              height={FIELD_HEIGHT}
              borderRadius='xs'
            />
          </Box>
        </Box>
      </Box>

      {/* Members (n) heading, the Invite or Add Member row, then member rows. */}
      <Skeleton variant='text' width={110} />
      <Box
        flexDirection='row'
        alignItems='center'
        marginTop='xs'
        marginBottom='s'
        style={{ gap: theme.spacing.s }}>
        <Skeleton
          variant='circle'
          width={INVITE_CIRCLE_SIZE}
          height={INVITE_CIRCLE_SIZE}
        />
        <Box flex={1}>
          <Skeleton variant='text' width='55%' />
        </Box>
      </Box>
      <SkeletonList row={renderMemberRowSkeleton} />
    </Box>
  );
};
